// Tipos compartilhados entre os sub-componentes da pagina Contas a Receber.
// Mantem assinaturas identicas ao monolitico original — qualquer mudanca
// aqui é breaking change pra quem consome.

export interface Parcela {
  id: string;
  contrato_id: string;
  contrato_numero: string;
  contrato_titulo: string;
  // Família do contrato (16/09/2026): a RAIZ da árvore de aditivos. Vem NULL
  // quando a parcela já é do contrato-raiz — economia de payload; quem lê usa
  // `contrato_raiz_id || contrato_id`.
  contrato_raiz_id?: string | null;
  contrato_raiz_numero?: string | null;
  contrato_raiz_titulo?: string | null;
  cliente_id: number | null;
  cliente_nome: string | null;
  numero_parcela: number | null;
  total_parcelas: number | null;
  descricao: string | null;
  valor_previsto: number;
  valor_recebido: number | null;
  data_vencimento: string;
  data_recebimento: string | null;
  status: string;
  nota_fiscal_numero: string | null;
  nota_fiscal_emissao: string | null;
  // FZ 0.1 — tri-estado do faturamento (fonte de verdade da "Faturada"):
  // emitida | dispensada ("não há NF") | pendente. A Marina marca Faturada
  // gravando nf_situacao='emitida', às vezes antes do número da nota.
  nf_situacao?: 'emitida' | 'dispensada' | 'pendente' | null;
  // FZ 0.5 — grupo de aglutinação: parcelas com o mesmo nf_grupo_id faturam em 1 NF.
  nf_grupo_id?: string | null;
  meio_pagamento: string | null;
  observacoes: string | null;
  criado_em: string;
  // Auditoria: origem da parcela (notion | conta_azul_api | sistema)
  origem?: 'notion' | 'conta_azul_api' | 'sistema' | null;
  // Sprint F15 — Quando TRUE, reconciliação Notion→CR PULA essa parcela.
  // Auto-marcada em qualquer edit no CR (atualizar, liberar, gerar BM, assinar).
  gerenciado_no_sistema?: boolean;
  id_externo_ca?: string | null;   // id da parcela no Conta Azul (se sincronizada)
  status_ca?: string | null;
  // Campos do Notion (Onda 3 — Contas a Receber import)
  notion_page_id?: string | null;
  parcela_titulo?: string | null;
  descricao_completa?: string | null;
  email_faturamento?: string | null;
  probabilidade?: string | null;        // Confirmado/Alta/Média/Baixa
  codigo_crea?: string | null;
  rateio_setores?: Record<string, number>;
  impostos?: Record<string, number>;
  valor_liquido_previsto?: number | null;
  valor_faturado?: number | null;
  // FIN-7ajustes (2026-07-22): desconto livre por parcela (glosa/saldo do
  // cliente) que reduz o líquido a receber; descrição do corpo da NF (auto
  // {objeto do contrato} — {parcela}, editável); objeto do contrato (só no detalhe).
  descontos?: number | null;
  descontos_motivo?: string | null;
  descricao_nf?: string | null;
  contrato_objeto?: string | null;
  // NF-CNAE (2026-07-27): código de serviço da NF (herda do serviço-fim, editável)
  cnae?: string | null;
  // FZ 5.2 — conta bancária da parcela (fluxo por banco)
  conta_financeira_id?: string | null;
  conta_financeira_apelido?: string | null;
  // Apelidos distintos das contas das entradas-filhas (agregado no fin_listar;
  // preenche o 🏦 da lista quando a própria parcela não tem conta definida)
  contas_filhos?: string | null;
  // Estado calculado no backend (rollout paralelo — campos opcionais).
  // liquido_a_receber segue FIN-MATCH-LIQ2 (líquido − descontos, piso 0);
  // numeric do node-pg pode chegar string — sempre passar por num().
  estado?: { previsto?: { liquido_a_receber?: number | string | null } } | null;
  // Dados cadastrais do cliente pro "Dados pra NF" (só no detalhe/fin_obter)
  cliente_cnpj?: string | null; cliente_endereco?: string | null;
  cliente_bairro?: string | null; cliente_cep?: string | null;
  cliente_cidade?: string | null; cliente_uf?: string | null;
  cliente_email?: string | null; cliente_fone?: string | null;
  cliente_cnae_codigo?: string | null; cliente_retentor_iss?: boolean | null;
  prazo_dias?: number | null;
  // Onda 4 — vínculo com venda + liberação
  tipo_parcela?: 'mobilizacao' | 'medicao' | 'saldo' | 'avulsa' | null;
  // F-CR-tipo: bucket pra filtro Diretor (só contrato) vs Marina (tudo)
  tipo_receita?: 'contrato' | 'reembolso_contrato' | 'receita_financeira' | 'aporte_capital' | 'outras_receitas' | 'transferencia_entre_contas' | 'mutuo_socio' | 'emprestimo_captado' | 'nao_classificada' | null;
  // Conciliação CR (P1 + regra 2026-07-08): entradas (filhos) agregam DENTRO
  // da parcela — a lista nunca mostra a entrada como linha.
  parcela_pai_id?: string | null;
  qtd_recebimentos?: number;
  recebido_filhos?: number;
  recebido_efetivo?: number | null;
  // Congelamento de contrato pausado + rastreio parcela -> venda (2026-07-08)
  contrato_status?: string | null;
  negocio_id?: number | null;
  venda_documento_id?: string | null;
  entregavel_id?: string | null;
  liberada?: boolean;
  liberada_em?: string | null;
  liberada_por?: 'contrato_assinado' | 'entrega_aprovada' | 'cliente_assinou_bm' | 'manual' | null;
  proposta_gerada_id?: string | null;
  // Onda 5b — BM como propriedade da parcela
  bm_link_hash?: string | null;
  bm_status?: 'gerado' | 'assinado_cliente' | 'cancelado' | null;
  bm_gerado_em?: string | null;
  bm_assinado_em?: string | null;
  bm_assinado_por_nome?: string | null;
  // Sprint F8 — destinatários da NF (array de { tipo, id|email })
  emails_nf_destinatarios?: Array<{ tipo: 'contato' | 'email'; id?: string; email?: string }>;
  // Duas datas (Lucas 2026-07-08): competência espelhada do CA — em parcela
  // de contrato a competência canônica segue nota_fiscal_emissao.
  data_competencia?: string | null;
  // F10.3e — detalhamento CA via /parcelas/{id}
  data_recebimento_real?: string | null;
  metodo_recebimento_ca?: string | null;
  conta_financeira_ca?: string | null;
  conciliado_ca?: boolean | null;
  // F-CR-contrato-externo: rotulo livre pra contratos nao cadastrados
  contrato_externo?: string | null;
  baixas_ca?: Array<{
    id?: string;
    data_pagamento?: string;
    valor_composicao?: {
      multa?: number; juros?: number; valor_bruto?: number;
      desconto?: number; taxa?: number; valor_liquido?: number;
    };
    conta_financeira?: { nome?: string };
    metodo_pagamento?: string;
    atualizado_em?: string;
  }> | null;
  // OFX vinculados via conciliacao bancaria (1 ou N transacoes OFX -> 1 parcela)
  qtd_ofx_vinculadas?: number;
  soma_ofx_vinculada?: number;
}

export type ColunaKey = 'contrato' | 'cliente' | 'parcela' | 'emissao' | 'vencimento' | 'previsto' | 'bm' | 'status' | 'nf' | 'origem' | 'acao';
export type DirecaoSort = 'asc' | 'desc';
export type CampoPeriodo = 'vencimento' | 'recebimento' | 'emissao';

export interface OfxVinculada {
  id: string;
  data_transacao: string;
  valor: number;
  banco: string;
  conta_id: string;
  conta_label: string;
  descricao: string;
  tipo_ofx: string;
}

export interface ContatoCliente {
  id: string;
  nome: string;
  cargo?: string | null;
  email: string | null;
  papel?: string | null;  // 'comercial' | 'financeiro' | 'ambos' | null
  principal?: boolean;
}

// ── Status efetivo de uma parcela do CR (derivação de exibição) ─────────────
// Toda tela que mostra status de parcela passa por aqui (lista do Financeiro,
// aba Financeiro do contrato). Cascata, na ordem — cada regra tem história:
//  1. "Recebida ganha de faturada" (Lucas 2026-07-15): dinheiro que entrou
//     (próprio valor_recebido OU agregado dos recebimentos-filhos em
//     recebido_efetivo) vence a regra de NF — parcela quitada mostra Recebida
//     (era o bug do 26041, recebido do CA aparecendo Faturada).
//  2. 'atrasada' GRAVADA no banco (sync antigo do Notion) com vencimento no
//     futuro rebaixa pra pendente (Lucas 2026-08-05: sem isso, avançar a data
//     não tirava a parcela de Atrasada). As regras 3/4 repromovem se couber.
//  3. NF preenchida promove pendente/atrasada → faturada. "Preenchida" =
//     número OU nf_situacao emitida/dispensada — a Marina marca Faturada às
//     vezes ANTES de ter o número (caso Flores da Cunha/CORSAN 2026-07-15).
//  4. pendente vencida → atrasada (nunca se grava — só exibição).
export function derivarStatusCR(p: any, hoje: string): string {
  let status = p.status;
  const num = (v: any) => (v == null ? 0 : parseFloat(String(v)) || 0);
  const prev = num(p.valor_previsto);
  const receb = num(p.recebido_efetivo ?? p.valor_recebido);
  if (status !== 'cancelada' && prev > 0 && receb > 0.005 && receb + 0.02 >= prev) {
    return 'recebida';
  }
  const venc = p.data_vencimento ? String(p.data_vencimento).slice(0, 10) : null;
  const temNf = (p.nota_fiscal_numero != null && String(p.nota_fiscal_numero).trim() !== '')
                || p.nf_situacao === 'emitida' || p.nf_situacao === 'dispensada';
  if (status === 'atrasada' && (!venc || venc >= hoje)) status = 'pendente';
  if (temNf && (status === 'pendente' || status === 'atrasada')) status = 'faturada';
  if (status === 'pendente' && venc && venc < hoje) status = 'atrasada';
  return status;
}
